import { Typography } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import LinearProgress from '@material-ui/core/LinearProgress';
import { withStyles } from '@material-ui/core/styles';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import LanguageAvatar from '../../core/LanguageAvatar';
import { globalStyles as styles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import { makeCancelable } from '../../core/utils/utils';
import Warning from '../../core/Warning';
import GS from '../../grafsync/grafsync';
import QualityCheck from '../../application-core/QualityCheck';

const CACHE_TIME = 180000;

const views = [
    "labelintegrity",
    "multiplepreflabels",
    "preflabelduplication",
    "conceptcycleclashes",
    "relationshipintegrity",
    "disjointclasses",
    "disjointclasseslabels",
    "monohierarchy",
    "multiplepropvalues",
    "multiplepropvalueslabels",
    "associatedpropertyrange",
    "associatedpropertyrangelabels",
    "asymmetric",
    "asymmetriclabels"
];

const viewsRequiringOntologies = [
    "multiplepropvalues",
    "multiplepropvalueslabels",
    "associatedpropertyrange",
    "associatedpropertyrangelabels",
    "asymmetric"
];

const viewsRequiringXL = [
    "multiplepropvalueslabels",
    "associatedpropertyrangelabels",
    "disjointclasseslabels",
    "asymmetriclabels"
];

const labelReport = [
    {
        prop: "prefAltLabelViolation",
        message: "The following values have been used for both preferred and alternative labels on the same concept."
    },
    {
        prop: "prefHiddenLabelViolation",
        message: "The following values have been used for both preferred and hidden labels on the same concept."
    },
    {
        prop: "altHiddenLabelViolation",
        message: "The following values have been used for both alternative and hidden labels on the same concept."
    },
    {
        prop: "altAltLabelViolation",
        message: "The following values have been used multiple times for alternative labels on the same concept."
    },
    {
        prop: "hiddenHiddenLabelViolation",
        message: "The following values have been used multiple times for hidden labels on the same concept."
    }
];

class SchemeQuality extends React.Component {

    constructor(props) {
        super(props);
        // We filter out views that require ontologies if no associated projects
        // We filter out XL views if no XL
        const filteredViews = views
            .filter(view => !viewsRequiringOntologies.includes(view)
                || (this.props.ontologyProjectAssociation && this.props.ontologyProjectAssociation.length > 0))
            .filter(view => !viewsRequiringXL.includes(view) || this.props.skosXL)
            .filter(view => view !== "monohierarchy" || this.props.data.editingMode === "Mono");
        let initialViewStates = {};
        filteredViews.forEach(fv => initialViewStates[fv] = undefined);
        const intiialState = Object.assign({
            viewCount: 0,
            isLoading: true,
            isErrored: false,
            isFailed: false,
            filteredViews
        }, initialViewStates);
        this.state = intiialState;
        this.cancellable = null;
    }

    componentDidMount() {
        this.loadViews();
    }

    async loadViews(refreshIfStale = true) {
        // We run linearly to reduce strain on the API
        const { filteredViews } = this.state;
        const currentTime = Date.now();
        const id = this.props.id;
        for (let i = 0; i < filteredViews.length; i++) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            const view = filteredViews[i];
            const viewID = view + "-" + this.props.graph + "-" + id;
            const cachedData = GS.retrieveCache(viewID);
            if ((!refreshIfStale && cachedData) || (cachedData && cachedData.time > currentTime - CACHE_TIME)) {
                this.setState((prevState) => ({
                    [view]: cachedData.data || undefined,
                    [view + "-time"]: cachedData.time,
                    viewCount: prevState.viewCount + 1
                }));
            }
            else {
                GS.clearCache(viewID);
                await this.loadData(view, i)
                    .promise
                    .then((schemeData) => {
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                        const viewTime = Date.now();
                        if (Object.keys(schemeData).length > 0) {
                            const data = schemeData.graph ? [...schemeData.graph] : [schemeData];
                            GS.cache(viewID, data);
                            this.setState({ [view]: data, [view + "-time"]: viewTime });
                        }
                        else {
                            GS.cache(viewID, null);
                            this.setState({ [view]: undefined, [view + "-time"]: viewTime });
                        }
                        this.setState((prevState) => ({
                            viewCount: prevState.viewCount + 1
                        }));
                    })
                    .catch(() => {
                        if (this.cancellable && !this.cancellable.isCancelled()) this.setState({
                            isErrored: true,
                            isLoading: false,
                            isFailed: true,
                            message: "Could not load all of the data for the quality view."
                        });
                        i = filteredViews.length;
                    });
            }
        }
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isLoading: false });
    }

    onExitWarning = () => this.setState({ isErrored: false });

    loadData(view, index) {
        this.cancellable = makeCancelable(
            GS.export(view, this.props.id, this.props.graph)
        );
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    onSelect(link) {
        this.props.history.push(link);
        this.props.onToggleView();
    }

    onRefresh = (view) => {
        this.setState({
            viewCount: 0,
            isLoading: true
        }, () => {
            GS.clearCache(view + "-" + this.props.graph + "-" + this.props.id);
            this.loadViews(false);
        });
    };

    render() {

        const { classes, link, ontologyProjectAssociation, data, skosXL } = this.props;
        const { isErrored, message, viewCount, isFailed, isLoading, asymmetric,
            labelintegrity, conceptcycleclashes, multiplepreflabels, disjointclasses, monohierarchy, multiplepropvalues,
            multiplepropvalueslabels, associatedpropertyrangelabels, disjointclasseslabels, asymmetriclabels,
            associatedpropertyrange, preflabelduplication, relationshipintegrity, filteredViews } = this.state;

        if (isErrored) return <Warning message={message} onExitWarning={this.onExitWarning} />;
        if (isFailed) return null;

        let viewItems = filteredViews.length;
        // If all checks are good then there will no state with errors for any of the views
        const checksSuccessful = !filteredViews.some(fv => this.state[fv]);

        return (
            <div style={{ margin: '8px' }}>
                <TitledPanel title="">
                    <div style={{ height: '93px', paddingTop: '40px' }}>
                        {viewCount < viewItems &&
                            <Typography color="secondary" paragraph className={classes.mainHeading} align="center">{viewCount}/{viewItems} Checks Completed</Typography>
                        }
                        {viewCount === viewItems &&
                            (checksSuccessful ?
                                <Typography
                                    paragraph
                                    className={classes.mainHeadingSecondary}
                                    style={{ color: '#50832C' }}
                                    align="center">
                                    <CheckCircleIcon
                                        data-test="checks-passed"
                                        fontSize="large"
                                        color="secondary"
                                        style={{ verticalAlign: 'middle', marginRight: '10px' }} />Checks Successful ({viewCount} run)</Typography>
                                :
                                <Typography
                                    data-test="checks-failed"
                                    paragraph
                                    className={classes.mainHeading}
                                    style={{ color: '#DD2C00' }}
                                    align="center">
                                    <WarningIcon
                                        data-test="checks-failed"
                                        fontSize="large"
                                        style={{ verticalAlign: 'middle', marginRight: '10px' }} />Checks Failed ({viewCount} run)</Typography>

                            )
                        }
                        <LinearProgress color="secondary" className={classes.progress} variant="determinate" value={viewCount / viewItems * 100} />
                    </div>
                </TitledPanel>
                <Typography className={classes.mainHeading} style={{ marginTop: '20px' }}>Quality Check Results</Typography>
                <Typography paragraph variant="body2" style={{ marginBottom: 36 }}>Results will be stored for three minutes. If you make fixes within that amount of time you may want to refresh that check to see the latest results.</Typography>
                <QualityCheck
                    dataTest="label-integrity"
                    title="Label Integrity Within a Concept"
                    issues={labelintegrity}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("labelintegrity")}
                    fromCache={this.state["labelintegrity-time"]}
                    issueDisplay={
                        (li) => {
                            return labelReport.map((lr, lrIndex) =>
                                li[lr.prop] &&
                                <div data-test={"lr-" + lrIndex} key={li.id + "-" + lr.prop} style={{ marginBottom: 10 }}>
                                    <Typography data-test="qm" variant="body2" paragraph>{lr.message}</Typography>
                                    <Chip
                                        color="primary"
                                        data-test="qm-resource"
                                        className={classes.activeChip}
                                        label={li.latestIRI || li.id}
                                        onClick={() => this.onSelect(link + "&view=concept&concept=" + li.id)} />
                                    <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {Object.keys(li[lr.prop]).sort().map(v => {
                                        const vLabels = Array.isArray(li[lr.prop][v]) ? li[lr.prop][v] : [li[lr.prop][v]];
                                        return vLabels.sort().map((vLabel, vIndex) => <Chip
                                            color="primary"
                                            className={classes.labelChip}
                                            data-test={"qm-chip-" + vIndex}
                                            key={"chip-" + vIndex}
                                            avatar={<LanguageAvatar lang={v} />}
                                            label={vLabel} />
                                        );
                                    })}
                                </div>
                            );
                        }
                    }
                />
                <QualityCheck
                    dataTest="multiple-preferred-labels"
                    title="Multiple Preferred Labels for the Same Language"
                    issues={multiplepreflabels}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("multiplepreflabels")}
                    fromCache={this.state["multiplepreflabels-time"]}
                    message="The following concepts have multiple preferred labels in the same language."
                    issueDisplay={
                        (li) => <div key={li.id} style={{ marginBottom: 10 }}>
                            <Chip color="primary"
                                className={classes.activeChip}
                                label={li.latestIRI || li.id}
                                onClick={() => this.onSelect(link + "&view=concept&concept=" + li.id)} />
                            <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            {Object.keys(li.multiplePrefLabelViolation).sort().map(v =>
                                li.multiplePrefLabelViolation[v].sort().map((text, c) =>
                                    <Chip color="primary"
                                        className={classes.labelChip}
                                        key={li.id + "-" + c}
                                        avatar={<LanguageAvatar lang={v} />}
                                        style={{ paddingLeft: '4px' }}
                                        label={text} />
                                )
                            )}
                        </div>
                    }
                />
                <QualityCheck
                    dataTest="same-preferred-label"
                    title="Same Preferred Label on Different Concepts"
                    issues={preflabelduplication}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("preflabelduplication")}
                    fromCache={this.state["preflabelduplication-time"]}
                    message="The following concepts have the same preferred label."
                    issueDisplay={
                        (li, liList) => <div key={li.id} style={{ marginBottom: 10 }}>
                            <Chip color="primary"
                                className={classes.activeChip}
                                label={li.latestIRI || li.id}
                                onClick={() => this.onSelect(link + "&view=concept&concept=" + li.id)} />
                            <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            <Chip color="primary"
                                className={classes.labelChip}
                                avatar={<LanguageAvatar lang={Object.keys(li.prefLabel)[0]} />}
                                style={{ paddingLeft: '4px' }}
                                label={li.prefLabel[Object.keys(li.prefLabel)[0]]} />
                            <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            {li.samePrefLabelViolation.sort().map(v => {
                                const vEntry = liList.find(li => li.id === v);
                                const vIRI = vEntry && vEntry.latestIRI ? vEntry.latestIRI : v;
                                return <Chip
                                    key={v}
                                    color="primary"
                                    className={classes.activeChip}
                                    label={vIRI}
                                    onClick={() => this.onSelect(link + "&view=concept&concept=" + v)} />;
                            })}
                        </div>
                    }
                />
                <QualityCheck
                    dataTest="concept-cycle"
                    title="Concept Cycle Violations"
                    issues={conceptcycleclashes}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("conceptcycleclashes")}
                    fromCache={this.state["conceptcycleclashes-time"]}
                    message={<span>The following pairs of concepts have broader <em>and</em> narrower relationships with each other.</span>}
                    issueDisplay={
                        (cc, ccList) => cc.cyclicWith && cc.cyclicWith.map(v => {
                            const vEntry = ccList.find(li => li.id === v);
                            const vIRI = vEntry && vEntry.latestIRI ? vEntry.latestIRI : v;
                            return <div key={v} style={{ marginBottom: 10 }}>
                                <Chip color="primary"
                                    className={classes.activeChip}
                                    clickable
                                    label={cc.latestIRI || cc.id}
                                    onClick={() => this.onSelect(link + "&view=concept&concept=" + cc.id)} />
                                <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                <Chip color="primary"
                                    className={classes.activeChip}
                                    clickable
                                    label={vIRI}
                                    onClick={() => this.onSelect(link + "&view=concept&concept=" + v)} />
                            </div>;
                        })
                    }
                />
                <QualityCheck
                    dataTest="associative-relationship"
                    title="Associative and Hierarchical Relationship Integrity"
                    issues={relationshipintegrity}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("relationshipintegrity")}
                    fromCache={this.state["relationshipintegrity-time"]}
                    message={<span>The following pairs of concepts have associative and hierarchical relationship conflicts.</span>}
                    issueDisplay={
                        (ri, riList) => {
                            let property = ri.narrowerRelatedRelationClash ? "narrowerRelatedRelationClash" : "broaderRelatedRelationClash";
                            return <div>
                                {ri[property] &&
                                    <Typography paragraph variant="body2">Via broader/narrower and related relationships:</Typography>
                                }
                                {ri[property] && ri[[property]].map(v => {
                                    const vEntry = riList.find(li => li.id === v);
                                    const vIRI = vEntry && vEntry.latestIRI ? vEntry.latestIRI : v;
                                    return <div key={v} style={{ marginBottom: 10 }}>
                                        <Chip color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={ri.latestIRI || ri.id}
                                            onClick={() => this.onSelect(link + "&view=concept&concept=" + ri.id)} />
                                        <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                        <Chip color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={vIRI}
                                            onClick={() => this.onSelect(link + "&view=concept&concept=" + v)} />
                                    </div>;
                                })}
                            </div>;
                        }}
                />
                <QualityCheck
                    dataTest="disjoint-classes"
                    title="Disjoint Classes on the Same Concept"
                    issues={disjointclasses}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("disjointclasses")}
                    fromCache={this.state["disjointclasses-time"]}
                    message={<span>The following concepts have disjoint classes.</span>}
                    issueDisplay={
                        (dw) => {
                            return dw.disjointClassViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <Chip color="primary"
                                        className={classes.activeChip}
                                        clickable
                                        label={dw.latestIRI || dw.id}
                                        onClick={() => this.onSelect(link + "&view=concept&concept=" + dw.id)} />
                                    <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {dw.disjointClassViolation.sort().map(v =>
                                        <Chip key={v} color="primary" className={classes.inactiveChip} label={v} />
                                    )}
                                </div>;
                        }}
                />
                {skosXL &&
                    <QualityCheck
                        dataTest="disjoint-classes-labels"
                        title="Disjoint Classes on the Same Label"
                        issues={disjointclasseslabels}
                        isLoading={isLoading}
                        onRefresh={() => this.onRefresh("disjointclasseslabels")}
                        fromCache={this.state["disjointclasseslabels-time"]}
                        message={<span>The following labels have disjoint classes.</span>}
                        issueDisplay={
                            (dw) => {
                                return dw.disjointClassViolation &&
                                    <div style={{ marginBottom: 10 }}>
                                        <Chip color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={dw.latestIRI || dw.id}
                                            onClick={() => this.onSelect(link + "&view=label&label=" + dw.id)} />
                                        <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                        {dw.disjointClassViolation.sort().map(v =>
                                            <Chip key={v} color="primary" className={classes.inactiveChip} label={v} />
                                        )}
                                    </div>;
                            }}
                    />
                }
                {data.editingMode === "Mono" && <QualityCheck
                    dataTest="monohierarchy"
                    title="Monohierarchy Violations"
                    issues={monohierarchy}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("monohierarchy")}
                    fromCache={this.state["monohierarchy-time"]}
                    message={<span>The following concepts are used in more than one place in the hierarchy.</span>}
                    issueDisplay={
                        (mh, mhList) => {
                            return mh.monohierarchyViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <Chip color="primary"
                                        className={classes.activeChip}
                                        clickable
                                        label={mh.latestIRI || mh.id}
                                        onClick={() => this.onSelect(link + "&view=concept&concept=" + mh.id)} />
                                    <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {mh.monohierarchyViolation.sort().map(v => {
                                        const vEntry = mhList.find(li => li.id === v);
                                        const vIRI = vEntry && vEntry.latestIRI ? vEntry.latestIRI : v;
                                        return <Chip key={v} color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={vIRI}
                                            onClick={() => this.onSelect(link + "&view=concept&concept=" + v)} />;
                                    })}
                                </div>;
                        }}
                />
                }
                {ontologyProjectAssociation &&
                    <QualityCheck
                        dataTest="multiple-property-values"
                        title="Multiple Property Values for a Functional Property on a Concept"
                        issues={multiplepropvalues}
                        isLoading={isLoading}
                        onRefresh={() => this.onRefresh("multiplepropvalues")}
                        fromCache={this.state["multiplepropvalues-time"]}
                        message="The following concepts have multiple values for the same property."
                        issueDisplay={
                            (li, issues) => {
                                return li.multiplePropValueViolation &&
                                    <div key={li.id} style={{ marginBottom: 10 }}>
                                        <Chip
                                            data-test="link"
                                            color="primary"
                                            className={classes.activeChip}
                                            label={li.latestIRI || li.id}
                                            onClick={() => this.onSelect(link + "&view=concept&concept=" + li.id)} />
                                        <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                        {li.multiplePropValueViolation.sort().map((v, vIndex) => {
                                            const labelItem = issues.find(item => item.id === v);
                                            const labelLang = Object.keys(labelItem.label)[0];
                                            const label = labelItem ?
                                                labelItem.label[labelLang]
                                                :
                                                v.latestIRI || v;
                                            return labelLang ?
                                                <Chip
                                                    data-test={"value-" + vIndex}
                                                    color="primary"
                                                    className={classes.labelChip}
                                                    key={"chip-" + v}
                                                    avatar={<LanguageAvatar lang={labelLang} />}
                                                    label={label} />
                                                :
                                                <Chip key={v} color="primary" className={classes.inactiveChip} label={label} />;
                                        }
                                        )}
                                    </div>;
                            }
                        }
                    />
                }
                {ontologyProjectAssociation && skosXL &&
                    <QualityCheck
                        dataTest="multiple-property-values-labels"
                        title="Multiple Property Values for a Functional Property on a Label"
                        issues={multiplepropvalueslabels}
                        isLoading={isLoading}
                        onRefresh={() => this.onRefresh("multiplepropvalueslabels")}
                        fromCache={this.state["multiplepropvalueslabels-time"]}
                        message="The following labels have multiple values for the same property."
                        issueDisplay={
                            (li, issues) => {
                                return li.multiplePropValueViolation &&
                                    <div key={li.id} style={{ marginBottom: 10 }}>
                                        <Chip
                                            data-test="link"
                                            color="primary"
                                            className={classes.activeChip}
                                            label={li.latestIRI || li.id}
                                            onClick={() => this.onSelect(link + "&view=label&label=" + li.id)} />
                                        <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                        {li.multiplePropValueViolation.sort().map((v, vIndex) => {
                                            const labelItem = issues.find(item => item.id === v);
                                            const labelLang = Object.keys(labelItem.label)[0];
                                            const label = labelItem ?
                                                labelItem.label[labelLang]
                                                :
                                                v.latestIRI || v;
                                            return labelLang ?
                                                <Chip
                                                    data-test={"value-" + vIndex}
                                                    color="primary"
                                                    className={classes.labelChip}
                                                    key={"chip-" + v}
                                                    avatar={<LanguageAvatar lang={labelLang} />}
                                                    label={label} />
                                                :
                                                <Chip key={v} color="primary" className={classes.inactiveChip} label={label} />;
                                        }
                                        )}
                                    </div>;
                            }
                        }
                    />
                }
                {ontologyProjectAssociation &&
                    <QualityCheck
                        dataTest="associated-property"
                        title="Associated Property Range Integrity on a Concept"
                        issues={associatedpropertyrange}
                        isLoading={isLoading}
                        onRefresh={() => this.onRefresh("associatedpropertyrange")}
                        fromCache={this.state["associatedpropertyrange-time"]}
                        message="The following concepts use an associated property whose target does not have classes that are stated on the range of the associated property."
                        issueDisplay={
                            (iwr) => {
                                return iwr.inconsistenWithRange && iwr.inconsistenWithRange.map(v =>
                                    <div key={v} style={{ marginBottom: 10 }}>
                                        <Chip color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={iwr.latestIRI || iwr.id}
                                            onClick={() => this.onSelect(link + "&view=concept&concept=" + iwr.id)} />
                                        <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                        <Chip color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={v} />
                                    </div>
                                );
                            }
                        }
                    />
                }
                {ontologyProjectAssociation && skosXL &&
                    <QualityCheck
                        dataTest="associated-property-labels"
                        title="Associated Property Range Integrity on a Label"
                        issues={associatedpropertyrangelabels}
                        isLoading={isLoading}
                        onRefresh={() => this.onRefresh("associatedpropertyrangelabels")}
                        fromCache={this.state["associatedpropertyrangelabels-time"]}
                        message="The following labels use an associated property whose target does not have classes that are stated on the range of the associated property."
                        issueDisplay={
                            (iwr) => {
                                return iwr.inconsistenWithRange && iwr.inconsistenWithRange.map(v =>
                                    <div key={v} style={{ marginBottom: 10 }}>
                                        <Chip color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={iwr.latestIRI || iwr.id}
                                            onClick={() => this.onSelect(link + "&view=label&label=" + iwr.id)} />
                                        <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                        <Chip color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={v} />
                                    </div>
                                );
                            }
                        }
                    />
                }
                {ontologyProjectAssociation &&
                    <QualityCheck
                        dataTest="asymmetric"
                        title="Asymmetric Relationship Integrity on a Concept"
                        issues={asymmetric}
                        isLoading={isLoading}
                        onRefresh={() => this.onRefresh("asymmetric")}
                        fromCache={this.state["asymmetric-time"]}
                        message="The following are connected in both directions by an asymmetric relationship."
                        issueDisplay={
                            (asy, asyList) => {
                                return asy.asymmetricViolation &&
                                    <div style={{ marginBottom: 10 }}>
                                        <Chip color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={asy.latestIRI || asy.id}
                                            onClick={() => this.onSelect(link + "&view=concept&concept=" + asy.id)} />
                                        <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                        {asy.asymmetricViolation.sort().map(v => {
                                            const vEntry = asyList.find(li => li.id === v);
                                            const vIRI = vEntry && vEntry.latestIRI ? vEntry.latestIRI : v;
                                            return <Chip key={v} color="primary" className={classes.inactiveChip} label={vIRI} />;
                                        })}
                                    </div>;
                            }
                        }
                    />
                }
                {ontologyProjectAssociation && skosXL &&
                    <QualityCheck
                        dataTest="asymmetric-labels"
                        title="Asymmetric Relationship Integrity on a Label"
                        issues={asymmetriclabels}
                        isLoading={isLoading}
                        onRefresh={() => this.onRefresh("asymmetriclabels")}
                        fromCache={this.state["asymmetriclabels-time"]}
                        message="The following are connected in both directions by an asymmetric relationship."
                        issueDisplay={
                            (asy, asyList) => {
                                return asy.asymmetricViolation &&
                                    <div style={{ marginBottom: 10 }}>
                                        <Chip color="primary"
                                            className={classes.activeChip}
                                            clickable
                                            label={asy.latestIRI || asy.id}
                                            onClick={() => this.onSelect(link + "&view=label&label=" + asy.id)} />
                                        <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                        {asy.asymmetricViolation.sort().map(v => {
                                            const vEntry = asyList.find(li => li.id === v);
                                            const vIRI = vEntry && vEntry.latestIRI ? vEntry.latestIRI : v;
                                            return <Chip key={v} color="primary" className={classes.inactiveChip} label={vIRI} />;
                                        })}
                                    </div>;
                            }
                        }
                    />
                }
            </div >
        );
    }
}

SchemeQuality.propTypes = {
    onToggleView: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    link: PropTypes.string.isRequired,
    skosXL: PropTypes.bool.isRequired,
    history: PropTypes.object,
    ontologyProjectAssociation: PropTypes.array,
    graph: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withRouter(SchemeQuality));